import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminOrdersAPI } from '../../Services/ECommerceAPI';
import { toast } from 'react-toastify';
import './AdminOrders.css';

const AdminOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, page, pageSize]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [ordersRes, statusesData] = await Promise.all([
                AdminOrdersAPI.getAllOrders(page, pageSize, null, selectedStatus || null),
                AdminOrdersAPI.getOrderStatuses()
            ]);

            const items = Array.isArray(ordersRes)
                ? ordersRes
                : (Array.isArray(ordersRes?.items) ? ordersRes.items : (Array.isArray(ordersRes?.data) ? ordersRes.data : []));

            const count = typeof ordersRes?.totalCount === 'number'
                ? ordersRes.totalCount
                : (typeof ordersRes?.total === 'number' ? ordersRes.total : (Array.isArray(ordersRes) ? ordersRes.length : items.length));

            setOrders(items);
            setTotalCount(count);
            setOrderStatuses(statusesData);
        } catch (error) {
            console.error('Error loading orders:', error);
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (orderId, statusId) => {
        try {
            await AdminOrdersAPI.updateOrderStatus(orderId, statusId);
            toast.success('Order status updated');
            loadData();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update order status');
        }
    };

    const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));

    return (
        <div className="admin-orders">
            <div className="page-header">
                <h1>Order Management</h1>
                <div className="filters">
                    <select
                        value={selectedStatus}
                        onChange={(e) => {
                            setSelectedStatus(e.target.value);
                            setPage(1);
                        }}
                        className="status-filter"
                    >
                        <option value="">All Statuses</option>
                        {orderStatuses.map(status => (
                            <option key={status.statusID} value={status.statusID}>
                                {status.statusName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading orders...</div>
            ) : (
                <div className="table-card admin-table-card">
                    <div className="table-header">
                        <div className="table-title-section">
                            <h3 className="table-title">Orders</h3>
                            <p className="table-subtitle">{totalCount} orders</p>
                        </div>
                    </div>

                    <div className="table-container orders-table-container">
                        <table className="orders-table admin-table">
                            <thead>
                                <tr>
                                    <th>Order Number</th>
                                    <th>Customer</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr key={order.orderID}>
                                        <td>{order.orderNumber}</td>
                                        <td>
                                            {order.customer?.firstName} {order.customer?.lastName}
                                            <br />
                                            <small>{order.customer?.email}</small>
                                        </td>
                                        <td>${order.totalAmount.toFixed(2)}</td>
                                        <td>
                                            <select
                                                value={order.orderStatusID}
                                                onChange={(e) => handleStatusUpdate(order.orderID, parseInt(e.target.value))}
                                                className="status-select"
                                            >
                                                {orderStatuses.map(status => (
                                                    <option key={status.statusID} value={status.statusID}>
                                                        {status.statusName}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>{new Date(order.orderDate).toLocaleDateString()}</td>
                                        <td>
                                            <button
                                                onClick={() => navigate(`/Home/OrderDetails/${order.orderID}`)}
                                                className="view-btn"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-footer">
                        <div className="table-meta">
                            Showing <strong>{orders.length}</strong> of <strong>{totalCount}</strong>
                        </div>
                        <div className="table-controls">
                            <label className="page-size">
                                Rows
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(parseInt(e.target.value, 10) || 15);
                                        setPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </label>
                            <div className="pagination">
                                <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                >
                                    Prev
                                </button>
                                <span className="page-indicator">
                                    Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
